import React, { useState, useEffect } from 'react';

const ANALYTICS_KEY = 'va-disable';

/**
 * Cookie/Privacy preferences modal.
 * The app uses localStorage (not cookies) for auth and preferences.
 * We use Vercel Analytics (no cookies, anonymized) - user can opt out via va-disable.
 */
function CookiePreferencesModal({ isOpen, onClose }) {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(() => localStorage.getItem(ANALYTICS_KEY) !== '1');

  useEffect(() => {
    if (!isOpen) return;
    setAnalyticsEnabled(localStorage.getItem(ANALYTICS_KEY) !== '1');
  }, [isOpen]);

  const handleAnalyticsToggle = (enabled) => {
    setAnalyticsEnabled(enabled);
    if (enabled) {
      localStorage.removeItem(ANALYTICS_KEY);
    } else {
      localStorage.setItem(ANALYTICS_KEY, '1');
    }
  };

  const handleSave = () => {
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[99999]">
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={handleSave}
        aria-hidden="true"
      />
      <div
        className="account-modal relative bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-xl border border-gray-200 dark:border-[#2a2a2a] w-full max-w-md mx-4 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
      >
        <div className="p-6">
          <h2 id="cookie-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Privacy &amp; Cookies
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            We use <strong>local storage</strong> (not cookies) for login and your preferences. For product improvement, we use anonymized analytics that do not track you across sites.
          </p>

          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 p-3 rounded-xl bg-gray-50 dark:bg-[#252525]">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Essential</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Required for login and preferences. Cannot be disabled.
                </p>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 pt-1">Always on</span>
            </div>

            <div className="flex items-start justify-between gap-4 p-3 rounded-xl bg-gray-50 dark:bg-[#252525]">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Analytics</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Anonymized usage data to improve the product. No personal identifiers.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analyticsEnabled}
                onClick={() => handleAnalyticsToggle(!analyticsEnabled)}
                className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors cursor-pointer ${analyticsEnabled ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-[#444]'}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white dark:bg-[#1a1a1a] shadow-sm transition-transform ${analyticsEnabled ? 'translate-x-4' : 'translate-x-0'}`}
                />
              </button>
            </div>
          </div>

          <a
            href="https://www.heymidus.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-4 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white underline"
          >
            Read our Privacy Policy
          </a>
        </div>
        <div className="px-6 pb-6 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            className="account-modal-btn px-4 py-2 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-black text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookiePreferencesModal;
